import { useFormState } from 'react-hook-form';
import { CheckboxElement, FormContainer } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import type { ChangeEvent } from 'react';

import { AlertError } from 'Common/AlertError';
import { EmailField, PasswordField } from 'Common/Form';
import { LoginSchema } from 'Common/Form/ValidationSchemas';
import { StyledAuthFormContainer, StyledButton } from 'Components/Styled';
import { useAppForm } from 'Utils/customHooks';

import type { ILogInRequest } from '@/libs/models';

const formFieldNames = {
  email: 'email',
  password: 'password',
  rememberMe: 'rememberMe'
} as const;

type Props = {
  onSubmit: (formData: ILogInRequest) => void;
  onEmailChange: (email: string) => void;
  error?: string | null;
};

export const LoginForm = ({ onSubmit, onEmailChange, error }: Readonly<Props>) => {
  const { t } = useTranslation();

  const formContext = useAppForm<ILogInRequest>(LoginSchema());
  const { isValid } = useFormState({ control: formContext.control });

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) =>
    onEmailChange(event.target.value);

  return (
    <FormContainer onSuccess={onSubmit} formContext={formContext}>
      <StyledAuthFormContainer>
        {error && <AlertError text={t(error)} />}

        <EmailField name={formFieldNames.email} onChange={handleEmailChange} />

        <PasswordField name={formFieldNames.password} />

        <CheckboxElement name={formFieldNames.rememberMe} label={t('auth.rememberMe')} />

        <StyledButton type="submit" size="large" disabled={!isValid}>
          {t('auth.login')}
        </StyledButton>
      </StyledAuthFormContainer>
    </FormContainer>
  );
};
